import { useMemo } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MONTH_WEEKS, MinAvgMaxKeys } from "@repo/utils";
import {
  useEstimateStructure,
  useEstimateEditorStore,
  useSummaryTimeInputTooltipAlert,
  useVisibilityModeCellClassName,
  useEstimateEditorSettings,
  ValueCell,
  ResetValueButton,
} from "@tools";
import { Grid } from "@material-ui/core";
import classnames from "classnames";

export const TimeCell = observer(({
  element,
  extendPadding,
  ...props
}) => {
  const { t } = useTranslation();
  const { visibilityMode } = useEstimateEditorStore();
  const { allowEdition, useProfitability } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  const tooltip = useSummaryTimeInputTooltipAlert(allowEdition);
  const className = useVisibilityModeCellClassName(structure.visibility.hideSummaryTime);
  
  const { displayAnyValue, displayWorkTypeTwoValues } = structure;
  const { useWeekTime, isTimelineTableUsed, maxValuesAsBuffer } = structure.settings;
  const { displayTime, displayTimeMax, hasFixedTime } = element;
  
  const editable = allowEdition && !useProfitability && !isTimelineTableUsed;
  
  const showMaxValue = useMemo(() => (
    !maxValuesAsBuffer && displayWorkTypeTwoValues[element.workTypeId] && (editable || displayTime !== displayTimeMax)
  ), [ maxValuesAsBuffer, displayWorkTypeTwoValues, editable, displayTime, displayTimeMax, maxValuesAsBuffer ]);
  
  const handleTimeChange = (time, isMax) => {
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && showMaxValue)
      key = MinAvgMaxKeys.MIN;
    
    element.setFixedTime({ [key]: time * (useWeekTime ? 1 : MONTH_WEEKS) });
  };
  
  const handleReset = () => {
    element.resetFixedTime();
  };
  
  return (
    <ValueCell
      size="large"
      displayContent
      showDecimals
      emptyInputs={!displayAnyValue}
      value={displayTime}
      valueMax={displayTimeMax}
      displayMax={showMaxValue}
      onValueChange={handleTimeChange}
      warning={hasFixedTime}
      editable={editable}
      className={className}
      textPresetClass="preset-summaryText"
      inputClassName="input-time"
      extendPadding={extendPadding || allowEdition || visibilityMode}
      innerClassName={classnames((allowEdition || visibilityMode) && "relative")}
      { ...props }
      { ...tooltip }
    >
      {
        allowEdition &&
        <Grid
          item container
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ right: -16 }}
        >
          {
            hasFixedTime &&
            <ResetValueButton action={handleReset}>
              {t("views.editor.revert_value")}
            </ResetValueButton>
          }
        </Grid>
      }
    </ValueCell>
  );
});

TimeCell.propTypes = {
  element: object.isRequired,
  extendPadding: bool,
};
